// frontend/src/pages/LoginPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link,
  useTheme,
  useMediaQuery,
  IconButton,
  InputAdornment,
  Fade,
  Zoom
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { validateEmail, validateText } from '../utils/validation';
import { tenantNameToSlug } from '../utils/tenantSlug';
import OAuthButtons from '../components/auth/OAuthButtons';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import ThemeToggle from '../components/common/ThemeToggle';
import IdentityConfirmDialog from '../components/identity/IdentityConfirmDialog';
import { readStationKey } from '../services/stationKey';
import {
  handoverSession,
  identityStateForCurrentUser,
  IDENTITY_ON,
  IDENTITY_UNKNOWN,
} from '../services/identityService';

function LoginPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { login, refreshUser, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [credentials, setCredentials] = useState({
    username: location.state?.email || '',
    password: '',
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  // WES Identity: rosto e PIN só existem numa máquina registrada como
  // estação. Fora dela (todo produto que não é a POC) a tela fica como era.
  const [isIdentityStation] = useState(() => Boolean(readStationKey()));
  // Segunda etapa do login numa estação: a senha já foi conferida, falta o
  // rosto (ou o PIN). Guarda quem digitou a senha, para conferir o rosto.
  const [identityPending, setIdentityPending] = useState(null);
  const [validationErrors, setValidationErrors] = useState({
    username: '',
    password: '',
  });
  const [touched, setTouched] = useState({
    username: false,
    password: false,
  });

  useEffect(() => {
    setMounted(true);
    // Add CSS animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-20px); }
      }
      
      @keyframes gradient {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      
      @keyframes fadeUp {
        from { transform: translateY(40px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      
      .gradient-bg {
        background: linear-gradient(180deg, #38BCFF 0%, #20A4FE 25%, #1991FA 50%, #0B82F9 75%, #016FF4 100%);
        background-size: 400% 400%;
        animation: gradient 15s ease infinite;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const s = location.state;
    if (s?.messageKey) {
      setMessage(t(s.messageKey));
    } else if (s?.message) {
      setMessage(s.message);
    } else {
      setMessage('');
    }
  }, [location.state, t, i18n.language]);

  const validateField = (name, value) => {
    let validation = { isValid: true, message: '' };
    
    switch (name) {
      case 'username':
        validation = validateEmail(value);
        break;
      case 'password':
        validation = validateText(value, 255, true);
        if (validation.isValid && value.length < 1) {
          validation = { isValid: false, message: t('auth.validation.passwordRequired') };
        }
        break;
      default:
        break;
    }
    
    return validation;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update credentials
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Validate field if it has been touched
    if (touched[name]) {
      const validation = validateField(name, value);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: validation.message,
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    
    // Validate field
    const validation = validateField(name, value);
    setValidationErrors((prev) => ({
      ...prev,
      [name]: validation.message,
    }));
  };

  // Depois de entrar — por senha, rosto ou PIN — o caminho é o mesmo: a
  // sessão do login por identidade é a mesma do login por senha (PBI-05),
  // então a escolha de organização e o destino também são.
  const continueAfterLogin = useCallback(async (userData, { viaIdentity = false } = {}) => {
    // Check if user has multiple tenants
    try {
      const response = await import('../services/apiClient').then(module => 
        module.default.get('/auth/tenants')
      );
      const {
        tenants,
        requires_organization_picker,
        has_multiple_tenants,
        is_superuser,
        is_platform_tenant_scope,
      } = response.data;

      // Admin WES (papel admin_wes) deve passar pelo seletor mesmo com 1 org;
      // o backend envia requires_organization_picker; fallback para clientes antigos.
      const picker =
        typeof requires_organization_picker === 'boolean'
          ? requires_organization_picker
          : Boolean(
              has_multiple_tenants ||
                is_superuser ||
                is_platform_tenant_scope
            );

      const needsTenantPicker =
        Array.isArray(tenants) && tenants.length > 0 && picker;

      if (needsTenantPicker) {
        navigate('/select-tenant', { replace: true });
        return;
      }
    } catch (tenantError) {
      console.warn('Failed to check tenant count, proceeding with single tenant flow:', tenantError);
    }
    
    // Check if we came from an organization URL or have a returnUrl
    const returnUrl = location.state?.returnUrl;
    const orgName = location.state?.orgName;
    
    let redirectPath;
    
    if (returnUrl) {
      // If we have a specific return URL, use it
      redirectPath = returnUrl;
    } else if (orgName && userData?.tenant_name) {
      const userOrgName = tenantNameToSlug(userData.tenant_name);
      if (orgName === userOrgName) {
        redirectPath = `/${orgName}`;
      } else {
        redirectPath = `/${userOrgName}`;
      }
    } else {
      redirectPath = userData?.tenant_name
        ? `/${tenantNameToSlug(userData.tenant_name)}`
        : '/dashboard';
      // WES Identity: quem confirmou a identidade na estação e trabalha em
      // Saúde vai direto para Saúde › Visão geral, como no protótipo.
      const canSeeHealth = (userData?.permissions || []).includes('health_care:read');
      if (viaIdentity && canSeeHealth && userData?.tenant_name) {
        redirectPath = `/${tenantNameToSlug(userData.tenant_name)}/health/overview`;
      }
    }
    
    // If came from another page, go back to that page, otherwise use calculated redirect.
    // Never navigate to `/` (App redireciona de volta para /login) nem para /login.
    const rawFrom = location.state?.from?.pathname;
    const from =
      rawFrom && rawFrom !== '/' && rawFrom !== '/login'
        ? rawFrom
        : redirectPath;
    navigate(from, { replace: true });
  }, [location.state, navigate]);

  // Rosto ou PIN confirmados: a sessão agora é a da estação (cookies novos),
  // e o AuthContext relê o usuário antes de seguir.
  const handleIdentityConfirmed = useCallback(async () => {
    setIdentityPending(null);
    setError('');
    try {
      const userData = await refreshUser();
      await continueAfterLogin(userData, { viaIdentity: true });
    } catch (identityError) {
      console.error('Identity login: failed to load session user', identityError);
      setError(t('identity.login.sessionNotLoaded'));
    }
  }, [refreshUser, continueAfterLogin, t]);

  // Fechar o pop-up é desistir do login: a senha sozinha não entra numa
  // estação, então a sessão que ela abriu não pode ficar.
  const handleIdentityCancel = useCallback(async () => {
    setIdentityPending(null);
    setCredentials((current) => ({ ...current, password: '' }));
    await logout();
  }, [logout]);

  // O rosto reconhecido é de outra pessoa: a sessão de estação que acabou de
  // abrir é dela, não de quem digitou a senha. Encerra as duas.
  const handleIdentityMismatch = useCallback(async () => {
    setIdentityPending(null);
    setCredentials((current) => ({ ...current, password: '' }));
    try { await handoverSession(); } catch { /* o logout abaixo limpa os cookies */ }
    await logout();
    setError(t('identity.confirm.errors.mismatch'));
  }, [logout, t]);

  // Credenciamento negado não é mensagem, é destino: a identidade já foi
  // confirmada, e a tela diz para quem e onde o acesso foi recusado.
  const handleIdentityDenied = useCallback(async (detail) => {
    setIdentityPending(null);
    await logout();
    navigate('/identity/access-denied', {
      replace: true,
      state: { fullName: detail.full_name || null, unit: detail.station_unit || null },
    });
  }, [logout, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate all fields before submission
    const usernameValidation = validateField('username', credentials.username);
    const passwordValidation = validateField('password', credentials.password);
    
    const errors = {
      username: usernameValidation.message,
      password: passwordValidation.message,
    };
    
    setValidationErrors(errors);
    setTouched({
      username: true,
      password: true,
    });
    
    // Check if any validation errors exist
    if (!usernameValidation.isValid || !passwordValidation.isValid) {
      setError(t('auth.validation.fixBeforeSubmit'));
      return;
    }
    
    setLoading(true);

    // A senha abriu sessao? Numa estacao, se a etapa de identidade nao puder
    // ser cumprida, essa sessao nao pode ficar de pe -- e antes ficava: o
    // `catch` mostrava erro de login com o usuario ja autenticado por tras.
    let sessaoAberta = false;

    try {
      // Use the AuthContext login function
      const userData = await login(credentials.username, credentials.password);
      sessaoAberta = true;

      // Numa estação, com a POC acesa para o tenant, a senha é só a primeira
      // etapa: quem tem rosto cadastrado confirma a identidade no pop-up, e
      // quem não tem faz o primeiro acesso. Fora disso, o login é o de sempre.
      if (isIdentityStation) {
        const estadoDaPoc = await identityStateForCurrentUser();

        // Não deu para saber se a POC está acesa. Numa estação isso NÃO pode
        // virar "entra só com a senha": seria a segunda etapa desaparecendo
        // por indisponibilidade, em silêncio (Bug #5145). Desfaz e diz o que
        // houve.
        if (estadoDaPoc === IDENTITY_UNKNOWN) {
          await logout();
          setCredentials((current) => ({ ...current, password: '' }));
          setError(t('identity.login.confirmationUnavailable'));
          return;
        }

        if (estadoDaPoc === IDENTITY_ON) {
          if (userData?.has_identity_binding) {
            setIdentityPending({
              email: credentials.username,
              userId: userData.user_id,
              displayName: userData.full_name || userData.name || '',
            });
          } else {
            navigate('/identity/first-access', { replace: true });
          }
          return;
        }
      }

      await continueAfterLogin(userData);
    } catch (error) {
      console.error('Login error:', error);

      if (sessaoAberta && isIdentityStation) {
        // Falhou depois da senha, numa estacao: desfaz, senao fica exatamente
        // o estado que a segunda etapa existe para impedir.
        try { await logout(); } catch { /* os cookies caem no proximo login */ }
      }

      if (error.response) {
        const detail = error.response.data?.detail;
        setError(
          detail ||
            t('auth.errors.loginFailedStatus', { status: error.response.status })
        );
      } else if (error.request) {
        setError(t('auth.errors.serverNotResponding'));
      } else {
        setError(t('auth.errors.requestError', { message: error.message }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="hub360-auth"
      sx={(t) => ({
      minHeight: '100vh',
      display: 'flex',
      position: 'relative',
      overflow: 'hidden',
      background:
        t.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #0a0a0f 0%, #000000 100%)'
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    })}
    >
      {/* Background decorations */}
      <Box sx={(t) => ({
        position: 'absolute',
        top: -200,
        right: -200,
        width: 600,
        height: 600,
        borderRadius: '50%',
        background:
          t.palette.mode === 'dark'
            ? 'radial-gradient(circle, rgba(1, 111, 244, 0.22) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(1, 111, 244, 0.1) 0%, transparent 70%)',
        filter: 'blur(40px)',
        animation: 'float 20s ease-in-out infinite'
      })} />
      <Box sx={(t) => ({
        position: 'absolute',
        bottom: -100,
        left: -100,
        width: 400,
        height: 400,
        borderRadius: '50%',
        background:
          t.palette.mode === 'dark'
            ? 'radial-gradient(circle, rgba(56, 188, 255, 0.15) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(56, 188, 255, 0.1) 0%, transparent 70%)',
        filter: 'blur(40px)',
        animation: 'float 15s ease-in-out infinite reverse'
      })} />
      
      {/* Left side - Branding */}
      {!isMobile && (
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#001035',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative elements */}
          <Box sx={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            filter: 'blur(30px)'
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            filter: 'blur(30px)'
          }} />
          
          <Fade in={mounted} timeout={1000}>
            <Box sx={{ textAlign: 'center', zIndex: 1 }}>
              <Box
                component="img"
                src="/images/wes-logo-white.svg"
                alt="WES Hub 360"
                sx={{
                  width: 280,
                  height: 'auto',
                  mb: 4,
                  animation: 'float 6s ease-in-out infinite'
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  letterSpacing: '-1px'
                }}
              >
                {t('auth.welcomeBack')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  opacity: 0.9,
                  fontWeight: 400,
                  maxWidth: 400,
                  mx: 'auto',
                  lineHeight: 1.6
                }}
              >
                {t('auth.continueJourney')}
              </Typography>
            </Box>
          </Fade>
        </Box>
      )}
      
      {/* Right side - Login form */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        position: 'relative',
        zIndex: 1
      }}>
        <Container maxWidth="sm">
          {/* Back + language */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <IconButton
              onClick={() => navigate('/')}
              sx={(th) => ({
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: th.palette.mode === 'dark' ? 'rgba(56, 188, 255, 0.1)' : 'rgba(1, 111, 244, 0.08)',
                },
              })}
              aria-label={t('common.back')}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ThemeToggle />
              <LanguageSwitcher variant="select" showLabel />
            </Box>
          </Box>
          
          <Zoom in={mounted} timeout={800}>
            <Paper
              elevation={0}
              sx={(th) => ({
                p: { xs: 4, md: 6 },
                borderRadius: 3,
                border: `1px solid ${th.palette.divider}`,
                background:
                  th.palette.mode === 'dark'
                    ? 'rgba(18, 18, 18, 0.92)'
                    : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow:
                  th.palette.mode === 'dark'
                    ? '0 20px 60px rgba(0, 0, 0, 0.5)'
                    : '0 20px 60px rgba(0, 0, 0, 0.12)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #38BCFF 0%, #016FF4 100%)',
                },
              })}
            >
              {/* Mobile logo */}
              {isMobile && (
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box
                    component="img"
                    src="/images/wes-logo-white.svg"
                    alt="WES Hub 360"
                    sx={(th) => ({
                      width: 180,
                      height: 'auto',
                      filter:
                        th.palette.mode === 'dark'
                          ? 'none'
                          : 'brightness(0) saturate(100%) invert(47%) sepia(82%) saturate(2341%) hue-rotate(217deg) brightness(94%) contrast(92%)',
                    })}
                  />
                </Box>
              )}
              
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    mb: 1,
                    textAlign: { xs: 'center', md: 'left' }
                  }}
                >
                  {t('auth.signIn')}
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ textAlign: { xs: 'center', md: 'left' } }}
                >
                  {t('auth.accessDashboard')}
                </Typography>
              </Box>
          
              {message && (
                <Alert 
                  severity="success" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      color: 'primary.light'
                    }
                  }} 
                  onClose={() => setMessage('')}
                >
                  {message}
                </Alert>
              )}
              
              {error && (
                <Alert
                  data-cy="login-error"
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                  }}
                >
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label={t('auth.emailAddress')}
                  name="username"
                  autoComplete="email"
                  autoFocus
                  value={credentials.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.username && !!validationErrors.username}
                  helperText={touched.username && validationErrors.username}
                  inputProps={{ 'data-cy': 'login-email' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'primary.main',
                    },
                  }}
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t('auth.password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={credentials.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && !!validationErrors.password}
                  helperText={touched.password && validationErrors.password}
                  inputProps={{ 'data-cy': 'login-password' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: 'primary.main' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 4,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'primary.main',
                    },
                  }}
                />
                
                <Button
                  data-cy="login-submit"
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading || !!validationErrors.username || !!validationErrors.password || !credentials.username || !credentials.password}
                  sx={(th) => ({
                    py: 2,
                    mb: 3,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                    // Desabilitado, o texto azul sobre o degradê azul quase
                    // sumia. Mantém o texto claro do botão e esmaece o todo.
                    '&.Mui-disabled': {
                      color: th.palette.primary.contrastText,
                      opacity: 0.6,
                    },
                  })}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: 'inherit' }} />
                  ) : (
                    t('auth.signIn')
                  )}
                </Button>

                <IdentityConfirmDialog
                  open={Boolean(identityPending)}
                  email={identityPending ? identityPending.email : ''}
                  displayName={identityPending ? identityPending.displayName : ''}
                  expectedUserId={identityPending ? identityPending.userId : null}
                  onConfirmed={handleIdentityConfirmed}
                  onCancel={handleIdentityCancel}
                  onDenied={handleIdentityDenied}
                  onMismatch={handleIdentityMismatch}
                />
                
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Link
                    data-cy="forgot-password-link"
                    component={RouterLink} 
                    to="/forgot-password"
                    sx={{
                      textDecoration: 'none',
                      color: 'primary.main',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      '&:hover': {
                        color: 'primary.dark',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </Box>

                {/* OAuth Buttons */}
                <OAuthButtons disabled={loading} />
              </Box>
            </Paper>
          </Zoom>
        </Container>
      </Box>
    </Box>
  );
}

LoginPage.displayName = 'LoginPage';

export default LoginPage;